// @ts-nocheck

import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Creates recruiting milestone events for a tracked college
 */
export const createCollegeTrackerEvents = async (
  profileId: string,
  collegeId: string,
  collegeName: string,
  trackerPhase: string
): Promise<boolean> => {
  try {
    // Only proceed if we have the required data
    if (!profileId || !collegeId || !collegeName) {
      console.error('Missing required data for tracker events');
      return false;
    }
    
    // Get today and standard dates for the events
    const today = new Date();
    const oneMonthLater = new Date();
    oneMonthLater.setMonth(today.getMonth() + 1);
    
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + 3);
    
    // Create milestone events based on tracker phase
    const eventsToCreate = [];
    
    // Check if events already exist for this college to avoid duplication
    const { data: existingEvents } = await supabase
      .from('calendar_events')
      .select('id')
      .eq('profile_id', profileId)
      .eq('related_entity_id', collegeId)
      .eq('source', 'tracker');
      
    if (existingEvents && existingEvents.length > 0) {
      console.log(`Events already exist for ${collegeName} - skipping creation`);
      return true;
    }
    
    // Application deadline - for interested or applied phase
    if (['interested', 'applied'].includes(trackerPhase)) {
      eventsToCreate.push({
        profile_id: profileId,
        title: `${collegeName} Application Deadline`,
        description: `Deadline to submit your application to ${collegeName}`,
        category: 'personal',  // Using valid category
        start_date: threeMonthsLater.toISOString().split('T')[0],
        end_date: null,
        is_custom: false,
        source: 'tracker',
        related_entity_id: collegeId
      });
    }
    
    // Coach follow-up - for all phases except committed or rejected
    if (!['committed', 'rejected'].includes(trackerPhase)) {
      eventsToCreate.push({
        profile_id: profileId,
        title: `Follow up with ${collegeName} Coach`,
        description: `Remember to follow up with the coaching staff at ${collegeName}`,
        category: 'personal',  // Using valid category
        start_date: oneMonthLater.toISOString().split('T')[0],
        end_date: null,
        is_custom: false,
        source: 'tracker',
        related_entity_id: collegeId
      });
    }
    
    // Visit dates - for interested, applied, or interviewing phases
    if (['interested', 'applied', 'interviewing'].includes(trackerPhase)) {
      const twoMonthsLater = new Date();
      twoMonthsLater.setMonth(today.getMonth() + 2);
      
      eventsToCreate.push({
        profile_id: profileId,
        title: `${collegeName} Campus Visit Window`,
        description: `Potential time frame to schedule a campus visit to ${collegeName}`,
        category: 'personal',  // Using valid category
        start_date: twoMonthsLater.toISOString().split('T')[0],
        end_date: threeMonthsLater.toISOString().split('T')[0],
        is_custom: false,
        source: 'tracker',
        related_entity_id: collegeId
      });
    }
    
    // Decision deadline - for offered phase
    if (trackerPhase === 'offered') {
      eventsToCreate.push({
        profile_id: profileId,
        title: `${collegeName} Decision Deadline`,
        description: `Deadline to accept or decline offer from ${collegeName}`,
        category: 'personal',  // Using valid category
        start_date: oneMonthLater.toISOString().split('T')[0],
        end_date: null,
        is_custom: false,
        source: 'tracker',
        related_entity_id: collegeId
      });
    }
    
    // Only insert events if we have any to add
    if (eventsToCreate.length > 0) {
      const { error } = await supabase
        .from('calendar_events')
        .insert(eventsToCreate);
        
      if (error) {
        console.error('Error creating tracker events:', error);
        return false;
      }
      
      console.log(`Created ${eventsToCreate.length} calendar events for ${collegeName}`);
      return true;
    }
    
    return true;
  } catch (error) {
    console.error('Error in createCollegeTrackerEvents:', error);
    return false;
  }
};

/**
 * Creates a calendar event for a saved college with deadline
 */
export const createSavedCollegeEvent = async (
  profileId: string,
  collegeId: string,
  collegeName: string,
  dueDate: string,
  eventType: string = 'Application',
  notes?: string
): Promise<boolean> => {
  try {
    // Check if event already exists for this college and due date
    const { data: existingEvents } = await supabase
      .from('calendar_events')
      .select('id')
      .eq('profile_id', profileId)
      .eq('related_entity_id', collegeId)
      .eq('source', 'saved_colleges')
      .eq('start_date', dueDate);
      
    // If an event already exists, we'll just update it
    if (existingEvents && existingEvents.length > 0) {
      const { error } = await supabase
        .from('calendar_events')
        .update({
          title: `${eventType} Deadline - ${collegeName}`,
          description: notes || `${eventType} deadline for ${collegeName}`,
        })
        .eq('id', existingEvents[0].id);
      
      if (error) {
        console.error('Error updating saved college event:', error);
        return false;
      }
      
      return true;
    }
    
    // Create a new event if none exists
    const { error } = await supabase
      .from('calendar_events')
      .insert({
        profile_id: profileId,
        title: `${eventType} Deadline - ${collegeName}`,
        description: notes || `${eventType} deadline for ${collegeName}`,
        category: 'personal',  // Using valid category
        start_date: dueDate,
        end_date: null,
        is_custom: false,
        source: 'saved_colleges',
        related_entity_id: collegeId
      });
      
    if (error) {
      console.error('Error creating saved college event:', error);
      return false;
    }
    
    console.log(`Created calendar event for ${collegeName} deadline`);
    return true;
  } catch (error) {
    console.error('Error in createSavedCollegeEvent:', error);
    return false;
  }
};

/**
 * Creates a calendar event for a saved camp
 */
export const createCampCalendarEvent = async (
  profileId: string,
  campId: string,
  campData: {
    title: string;
    start_date: string;
    end_date?: string | null;
    location?: string | null;
    description?: string | null;
  }
): Promise<boolean> => {
  try {
    // Check if event already exists for this camp
    const { data: existingEvents } = await supabase
      .from('calendar_events')
      .select('id')
      .eq('profile_id', profileId)
      .eq('related_entity_id', campId)
      .eq('source', 'camp');
      
    if (existingEvents && existingEvents.length > 0) {
      console.log(`Camp event already exists for ${campData.title} - skipping creation`);
      return true;
    }
    
    // Create the camp calendar event
    const { error } = await supabase
      .from('calendar_events')
      .insert({
        profile_id: profileId,
        title: campData.title,
        description: campData.description || `Camp at ${campData.location || ''}`,
        category: 'camp',  // Using valid category 
        start_date: campData.start_date,
        end_date: campData.end_date || null,
        is_custom: false,
        source: 'camp',
        related_entity_id: campId
      });
      
    if (error) {
      console.error('Error creating camp event:', error);
      return false;
    }
    
    console.log(`Created calendar event for camp: ${campData.title}`);
    return true;
  } catch (error) {
    console.error('Error in createCampCalendarEvent:', error);
    return false;
  }
};